import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { getITTickets, createITTicket, updateITTicket, addTicketComment, getClients, getEmployees, getITLocations, getITTicket, generateVisitFromTicket, getITAssets } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Ticket, Plus, MagnifyingGlass, Eye, PencilSimple, ChatCircle, CalendarCheck, User, Clock, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TICKET_CATEGORIES = [
  { id: 'hardware', name: 'Hardware' }, { id: 'software', name: 'Software' },
  { id: 'network', name: 'Red' }, { id: 'printers', name: 'Impresoras' },
  { id: 'email', name: 'Correo' }, { id: 'access', name: 'Acceso' },
  { id: 'server', name: 'Servidor' }, { id: 'security', name: 'Seguridad' },
  { id: 'backup', name: 'Backup' }, { id: 'licenses', name: 'Licencias' }
];
const TICKET_STATUSES = [
  { id: 'open', name: 'Nuevo' },
  { id: 'assigned', name: 'Asignado' },
  { id: 'in_progress', name: 'En Progreso' },
  { id: 'on_hold', name: 'En Espera' },
  { id: 'resolved', name: 'Resuelto' },
  { id: 'closed', name: 'Cerrado' },
  { id: 'cancelled', name: 'Cancelado' },
];
const PRIORITIES = [
  { id: 'critical', name: 'Crítica', color: 'bg-red-500/20 text-red-400' },
  { id: 'high', name: 'Alta', color: 'bg-orange-500/20 text-orange-400' },
  { id: 'medium', name: 'Media', color: 'bg-yellow-500/20 text-yellow-400' },
  { id: 'low', name: 'Baja', color: 'bg-green-500/20 text-green-400' }
];

const getTicketStats = (items) => items.reduce((acc, ticket) => {
  const status = ticket.status;
  acc.total += 1;
  if (status) acc[status] = (acc[status] || 0) + 1;
  return acc;
}, { total: 0 });

const getStatusLabel = (status) => TICKET_STATUSES.find(item => item.id === status)?.name || status || '';
const getPriorityLabel = (priority) => PRIORITIES.find(item => item.id === priority)?.name || priority || '';

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [search, setSearch] = useState('');
  const [commentText, setCommentText] = useState('');
  const [form, setForm] = useState({
    client_id: '', location_id: '', end_user: '', category: 'hardware',
    priority: 'medium', title: '', description: '', asset_id: ''
  });

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority && filterPriority !== 'all') params.priority = filterPriority;
      const res = await getITTickets(params);
      const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setTickets(items);
      setStats(res.data?.stats || getTicketStats(items));
    } catch (err) { toast.error('Error loading tickets'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterPriority]);

  useEffect(() => { loadTickets(); }, [loadTickets]);
  useEffect(() => {
    Promise.all([getClients(), getEmployees()]).then(([c, e]) => {
      setClients(c.data || []);
      setEmployees(e.data || []);
    }).catch(() => {});
  }, []);

  const loadClientLocations = async (cid) => {
    if (!cid) { setLocations([]); return; }
    try { const r = await getITLocations({ client_id: cid }); setLocations(Array.isArray(r.data) ? r.data : (r.data?.items || [])); } catch { setLocations([]); }
  };
  const loadClientAssets = async (cid) => {
    if (!cid) { setAssets([]); return; }
    try { const r = await getITAssets({ client_id: cid }); setAssets(Array.isArray(r.data) ? r.data : (r.data?.items || [])); } catch { setAssets([]); }
  };

  const handleCreate = () => {
    setForm({ client_id: '', location_id: '', end_user: '', category: 'hardware', priority: 'medium', title: '', description: '', asset_id: '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createITTicket(form);
      toast.success('Ticket creado');
      setShowModal(false);
      loadTickets();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openDetail = async (ticketId) => {
    setDetailLoading(true);
    try {
      const res = await getITTicket(ticketId);
      setShowDetail(res.data);
    } catch { toast.error('Error'); }
    finally { setDetailLoading(false); }
  };

  const handleAssign = async (ticketId, empId) => {
    try {
      await updateITTicket(ticketId, { assigned_to: empId, status: 'assigned' });
      toast.success('Ticket asignado');
      openDetail(ticketId);
      loadTickets();
    } catch { toast.error('Error'); }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await updateITTicket(ticketId, { status });
      toast.success(`Estado: ${getStatusLabel(status)}`);
      openDetail(ticketId);
      loadTickets();
    } catch { toast.error('Error'); }
  };

  const handleAddComment = async (ticketId) => {
    if (!commentText.trim()) return;
    try {
      await addTicketComment(ticketId, { comment: commentText, is_internal: false });
      setCommentText('');
      toast.success('Comentario agregado');
      openDetail(ticketId);
    } catch { toast.error('Error'); }
  };

  const handleGenerateVisit = async (ticketId) => {
    try {
      const res = await generateVisitFromTicket(ticketId);
      toast.success(`Visita ${res.data.visit_number} generada`);
      openDetail(ticketId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const statusColor = (e) => {
    const c = { open: 'bg-blue-500/20 text-blue-400', assigned: 'bg-cyan-500/20 text-cyan-400', in_progress: 'bg-purple-500/20 text-purple-400', on_hold: 'bg-yellow-500/20 text-yellow-400', resolved: 'bg-green-500/20 text-green-400', closed: 'bg-gray-500/20 text-gray-400', cancelled: 'bg-red-500/20 text-red-400' };
    return c[e] || 'bg-gray-500/20 text-gray-400';
  };
  const prioColor = (p) => (PRIORITIES.find(pr => pr.id === p)?.color) || 'bg-gray-500/20 text-gray-400';

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Ticket className="text-[#3AB8D6]" size={28} /> Mesa de Ayuda / Tickets
            </h1>
            <p className="text-gray-400 text-sm mt-1">Gestión de incidentes y solicitudes de soporte</p>
          </div>
          <Button onClick={handleCreate} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
            <Plus size={18} className="mr-2" /> Nuevo Ticket
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
          {[
            { label: 'Total', value: stats.total || 0, c: 'text-white' },
            { label: 'Nuevos', value: stats.open || 0, c: 'text-blue-400' },
            { label: 'Asignados', value: stats.assigned || 0, c: 'text-cyan-400' },
            { label: 'En Progreso', value: stats.in_progress || 0, c: 'text-purple-400' },
            { label: 'En Espera', value: stats.on_hold || 0, c: 'text-yellow-400' },
            { label: 'Resueltos', value: stats.resolved || 0, c: 'text-green-400' },
            { label: 'Cerrados', value: stats.closed || 0, c: 'text-gray-400' }
          ].map((s, i) => (
            <div key={i} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-2 text-center">
              <div className={`text-lg font-bold ${s.c}`}>{s.value}</div>
              <div className="text-[10px] text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input placeholder="Buscar tickets..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[#1a1d23] border-gray-700 text-white" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px] bg-[#1a1d23] border-gray-700 text-white"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todos</SelectItem>
              {TICKET_STATUSES.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[150px] bg-[#1a1d23] border-gray-700 text-white"><SelectValue placeholder="Prioridad" /></SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todas</SelectItem>
              {PRIORITIES.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Tickets List */}
        <div className="space-y-2">
          {loading ? (
            <div className="flex justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E2B05E]"></div></div>
          ) : tickets.length === 0 ? (
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg text-center py-12 text-gray-500">
              <Ticket size={48} className="mx-auto mb-3 opacity-50" /><p>No hay tickets</p>
            </div>
          ) : tickets.map((t) => (
            <div key={t._id} onClick={() => openDetail(t._id)} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-4 cursor-pointer hover:border-gray-700 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-semibold text-sm">{t.ticket_number}</span>
                    <Badge className={statusColor(t.status)}>{getStatusLabel(t.status)}</Badge>
                    <Badge className={prioColor(t.priority)}>{getPriorityLabel(t.priority)}</Badge>
                    <Badge className="bg-gray-600/20 text-gray-400 text-xs">{t.category}</Badge>
                  </div>
                  <div className="text-sm text-gray-300">{t.title}</div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{t.client_name}</span>
                    {t.assigned_name && <span className="flex items-center gap-1"><User size={10} />{t.assigned_name}</span>}
                    {t.comment_count > 0 && <span className="flex items-center gap-1"><ChatCircle size={10} />{t.comment_count}</span>}
                    <span className="flex items-center gap-1"><Clock size={10} />{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-[#E2B05E]">Nuevo Ticket</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Cliente *</Label>
                  <Select value={form.client_id} onValueChange={(v) => { setForm(p => ({ ...p, client_id: v })); loadClientLocations(v); loadClientAssets(v); }}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {clients.map(c => <SelectItem key={c._id} value={c._id}>{c.name || c.company}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Sucursal</Label>
                  <Select value={form.location_id} onValueChange={(v) => setForm(p => ({ ...p, location_id: v }))}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {locations.map(l => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Categoría</Label>
                  <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {TICKET_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Prioridad</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm(p => ({ ...p, priority: v }))}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {PRIORITIES.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Usuario Final</Label>
                  <Input value={form.end_user} onChange={(e) => setForm(p => ({ ...p, end_user: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Activo Relacionado</Label>
                  <Select value={form.asset_id} onValueChange={(v) => setForm(p => ({ ...p, asset_id: v }))}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white"><SelectValue placeholder="Opcional" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      <SelectItem value="none">Ninguno</SelectItem>
                      {assets.map(a => <SelectItem key={a._id} value={a._id}>{a.name || a.tag || a.serial_number || `${a.brand || ''} ${a.model || ''}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Asunto *</Label>
                <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Descripción</Label>
                <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" rows={4} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">Crear Ticket</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            {showDetail && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-[#E2B05E] flex items-center gap-2">
                    {showDetail.ticket_number}
                    <Badge className={statusColor(showDetail.status)}>{getStatusLabel(showDetail.status)}</Badge>
                    <Badge className={prioColor(showDetail.priority)}>{getPriorityLabel(showDetail.priority)}</Badge>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {showDetail.status === 'open' && (
                      <div className="flex gap-2 items-center">
                        <Select onValueChange={(v) => handleAssign(showDetail._id, v)}>
                          <SelectTrigger className="w-[200px] bg-[#12141a] border-gray-700 text-white text-xs"><SelectValue placeholder="Asignar técnico" /></SelectTrigger>
                          <SelectContent className="bg-[#1a1d23] border-gray-700">
                            {employees.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {['assigned', 'on_hold'].includes(showDetail.status) && (
                      <Button size="sm" onClick={() => handleStatusChange(showDetail._id, 'in_progress')} className="bg-purple-500/20 text-purple-400 text-xs">En Progreso</Button>
                    )}
                    {showDetail.status === 'in_progress' && (
                      <>
                        <Button size="sm" onClick={() => handleStatusChange(showDetail._id, 'resolved')} className="bg-green-500/20 text-green-400 text-xs">Resolver</Button>
                        <Button size="sm" onClick={() => handleStatusChange(showDetail._id, 'on_hold')} className="bg-yellow-500/20 text-yellow-400 text-xs">En Espera</Button>
                      </>
                    )}
                    {showDetail.status === 'resolved' && (
                      <Button size="sm" onClick={() => handleStatusChange(showDetail._id, 'closed')} className="bg-gray-500/20 text-gray-400 text-xs">Cerrar</Button>
                    )}
                    {!showDetail.generated_visit_id && ['open', 'assigned', 'in_progress'].includes(showDetail.status) && (
                      <Button size="sm" onClick={() => handleGenerateVisit(showDetail._id)} variant="outline" className="border-[#E2B05E]/50 text-[#E2B05E] text-xs">
                        <CalendarCheck size={14} className="mr-1" /> Generar Visita
                      </Button>
                    )}
                  </div>

                  {/* Info */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      ['Cliente', showDetail.client_name],
                      ['RUC', showDetail.client_ruc],
                      ['Categoría', showDetail.category],
                      ['Usuario Final', showDetail.end_user],
                      ['Asignado a', showDetail.assigned_name],
                      ['Creado', showDetail.created_at ? new Date(showDetail.created_at).toLocaleString() : '']
                    ].filter(([,v]) => v).map(([l, v]) => (
                      <div key={l} className="bg-[#12141a] rounded p-2">
                        <span className="text-gray-500 text-xs">{l}</span>
                        <div className="text-gray-300">{v}</div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#12141a] rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Asunto</div>
                    <div className="text-white font-medium">{showDetail.title}</div>
                    {showDetail.description && <p className="text-gray-400 text-sm mt-2 whitespace-pre-wrap">{showDetail.description}</p>}
                  </div>

                  {/* Comments */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-300 mb-2">Comentarios ({(showDetail.comments || []).length})</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {(showDetail.comments || []).map(c => (
                        <div key={c._id || c.created_at} className={`bg-[#12141a] rounded-lg p-3 ${c.is_internal ? 'border-l-2 border-yellow-500' : ''}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-[#3AB8D6]">{c.author_name || c.author_email}</span>
                            <span className="text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                          </div>
                          <p className="text-sm text-gray-300">{c.comment}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Agregar comentario..." className="bg-[#12141a] border-gray-700 text-white flex-1" onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(showDetail._id); }} />
                      <Button onClick={() => handleAddComment(showDetail._id)} className="bg-[#3AB8D6] text-black">
                        <ChatCircle size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
